import { parse } from 'graphql';

import { createAuthenticatedGraphQLClient } from '../auth/createAuthenticatedGraphQLClient';
import type { RuntimeEnvironment } from '../graphql/config/runtimeConfig';
import { resolveClientEnv } from '../graphql/config/resolveClientEnv';
import type {
  CreateDoorVisitReportMutation,
  CreateDoorVisitReportMutationVariables,
} from '../graphql/queries/createDoorVisitReport.generated';
import type {
  DeleteDoorVisitReportMutation,
  DeleteDoorVisitReportMutationVariables,
} from '../graphql/queries/deleteDoorVisitReport.generated';
import type {
  DoorVisitReportDownloadQuery,
  DoorVisitReportDownloadQueryVariables,
} from '../graphql/queries/doorVisitReportDownload.generated';
import type { DoorVisitReportRunQuery, DoorVisitReportRunQueryVariables } from '../graphql/queries/doorVisitReportRun.generated';
import type { DoorVisitReportRunsQuery, DoorVisitReportRunsQueryVariables } from '../graphql/queries/doorVisitReportRuns.generated';
import type {
  RetryDoorVisitReportMutation,
  RetryDoorVisitReportMutationVariables,
} from '../graphql/queries/retryDoorVisitReport.generated';
import { buildDoorVisitNormalizedQueryVariables, type DoorVisitFilterState } from './filters/doorVisitFilters';

const REPORT_RUN_FIELDS = /* GraphQL */ `
  id
  status
  attempt
  totalRows
  processedRows
  failureSummary
  filterSummary
  requesterName
  createdAt
  updatedAt
  startedAt
  completedAt
  failedAt
`;

// Codegen emits only types. Keep these authenticated runtime documents in sync
// with their .graphql sources; doorVisitReportApi.test.ts enforces AST parity.
export const CREATE_DOOR_VISIT_REPORT_DOCUMENT = parse(/* GraphQL */ `
  mutation CreateDoorVisitReport($input: CreateDoorVisitReportInput!) {
    createDoorVisitReport(input: $input) {
      ${REPORT_RUN_FIELDS}
    }
  }
`);

export const DOOR_VISIT_REPORT_RUN_DOCUMENT = parse(/* GraphQL */ `
  query DoorVisitReportRun($reportId: ID!) {
    doorVisitReportRun(reportId: $reportId) {
      ${REPORT_RUN_FIELDS}
    }
  }
`);

export const DOOR_VISIT_REPORT_RUNS_DOCUMENT = parse(/* GraphQL */ `
  query DoorVisitReportRuns($first: Int, $offset: Int) {
    doorVisitReportRuns(first: $first, offset: $offset) {
      total
      items {
        ${REPORT_RUN_FIELDS}
      }
    }
  }
`);

export const RETRY_DOOR_VISIT_REPORT_DOCUMENT = parse(/* GraphQL */ `
  mutation RetryDoorVisitReport($reportId: ID!, $requestId: ID!) {
    retryDoorVisitReport(reportId: $reportId, requestId: $requestId) {
      ${REPORT_RUN_FIELDS}
    }
  }
`);

export const DOOR_VISIT_REPORT_DOWNLOAD_DOCUMENT = parse(/* GraphQL */ `
  query DoorVisitReportDownload($reportId: ID!) {
    doorVisitReportDownload(reportId: $reportId) {
      url
      expiresAt
      fileName
    }
  }
`);

export const DELETE_DOOR_VISIT_REPORT_DOCUMENT = parse(/* GraphQL */ `
  mutation DeleteDoorVisitReport($reportId: ID!) {
    deleteDoorVisitReport(reportId: $reportId)
  }
`);

export type DoorVisitReportRun = CreateDoorVisitReportMutation['createDoorVisitReport'];
export type DoorVisitReportDownload = DoorVisitReportDownloadQuery['doorVisitReportDownload'];
export type DoorVisitReportFetchImpl = Parameters<typeof createAuthenticatedGraphQLClient>[0]['fetchImpl'];

export type DoorVisitReportRequestOptions = {
  signal?: AbortSignal;
};

export type DoorVisitReportApi = {
  createReport: (variables: CreateDoorVisitReportMutationVariables, options?: DoorVisitReportRequestOptions) => Promise<DoorVisitReportRun>;
  getReportRun: (reportId: string, options?: DoorVisitReportRequestOptions) => Promise<DoorVisitReportRun>;
  getReportRuns: (
    variables: DoorVisitReportRunsQueryVariables,
    options?: DoorVisitReportRequestOptions,
  ) => Promise<DoorVisitReportRunsQuery['doorVisitReportRuns']>;
  retryReport: (reportId: string, requestId: string, options?: DoorVisitReportRequestOptions) => Promise<DoorVisitReportRun>;
  getReportDownload: (reportId: string, options?: DoorVisitReportRequestOptions) => Promise<DoorVisitReportDownload>;
  deleteReport: (reportId: string, options?: DoorVisitReportRequestOptions) => Promise<boolean>;
};

export type CreateDoorVisitReportApiOptions = {
  env?: RuntimeEnvironment;
  fetchImpl?: DoorVisitReportFetchImpl;
};

// This is intentionally pagination-free. buildDoorVisitNormalizedQueryVariables
// is the same URL-filter normalization used by the paginated Door Visit list.
export const buildCreateDoorVisitReportVariables = (
  filters: DoorVisitFilterState,
  requestId: string,
): CreateDoorVisitReportMutationVariables => ({
  input: {
    requestId,
    ...buildDoorVisitNormalizedQueryVariables(filters),
  },
});

export const createDoorVisitReportApi = ({
  env = resolveClientEnv(),
  fetchImpl,
}: CreateDoorVisitReportApiOptions = {}): DoorVisitReportApi => {
  const client = createAuthenticatedGraphQLClient({ env, fetchImpl });

  return {
    async createReport(variables, options = {}) {
      const data = await client.request<CreateDoorVisitReportMutation>({
        document: CREATE_DOOR_VISIT_REPORT_DOCUMENT,
        variables,
        signal: options.signal,
      });
      return data.createDoorVisitReport;
    },
    async getReportRun(reportId, options = {}) {
      const variables: DoorVisitReportRunQueryVariables = { reportId };
      const data = await client.request<DoorVisitReportRunQuery>({
        document: DOOR_VISIT_REPORT_RUN_DOCUMENT,
        variables,
        signal: options.signal,
      });
      return data.doorVisitReportRun;
    },
    async getReportRuns(variables, options = {}) {
      const data = await client.request<DoorVisitReportRunsQuery>({
        document: DOOR_VISIT_REPORT_RUNS_DOCUMENT,
        variables,
        signal: options.signal,
      });
      return data.doorVisitReportRuns;
    },
    async retryReport(reportId, requestId, options = {}) {
      const variables: RetryDoorVisitReportMutationVariables = { reportId, requestId };
      const data = await client.request<RetryDoorVisitReportMutation>({
        document: RETRY_DOOR_VISIT_REPORT_DOCUMENT,
        variables,
        signal: options.signal,
      });
      return data.retryDoorVisitReport;
    },
    async getReportDownload(reportId, options = {}) {
      const variables: DoorVisitReportDownloadQueryVariables = { reportId };
      const data = await client.request<DoorVisitReportDownloadQuery>({
        document: DOOR_VISIT_REPORT_DOWNLOAD_DOCUMENT,
        variables,
        signal: options.signal,
      });
      return data.doorVisitReportDownload;
    },
    async deleteReport(reportId, options = {}) {
      const variables: DeleteDoorVisitReportMutationVariables = { reportId };
      const data = await client.request<DeleteDoorVisitReportMutation>({
        document: DELETE_DOOR_VISIT_REPORT_DOCUMENT,
        variables,
        signal: options.signal,
      });
      return data.deleteDoorVisitReport;
    },
  };
};
